import React, { useEffect, useState } from 'react';
import { Card, Image, Typography, Button, Spin, Divider, Space, Tag, message, Alert, Steps } from 'antd';
import { QrcodeOutlined, DownloadOutlined, ShareAltOutlined, WechatOutlined, SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { wxApi, wecomApi } from '../../services/api';
import useAuthStore from '../../store/authStore';

const { Title, Text, Paragraph } = Typography;

export default function QRCodePage() {
  const user = useAuthStore((s) => s.user);
  const [wecomQR, setWecomQR] = useState(null);
  const [miniappQR, setMiniappQR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wecomError, setWecomError] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    // 优先加载企微"联系我"二维码，失败则回退到小程序二维码
    Promise.allSettled([
      wecomApi.getContactQRCode(),
      wxApi.getQrCode(),
    ]).then(([wecomResult, miniappResult]) => {
      if (wecomResult.status === 'fulfilled' && wecomResult.value?.qrCode) {
        setWecomQR(wecomResult.value);
      } else {
        setWecomError(wecomResult.reason?.response?.data?.error || '企微未配置或账号未绑定');
      }
      if (miniappResult.status === 'fulfilled') {
        setMiniappQR(miniappResult.value);
      }
    }).finally(() => setLoading(false));
  }, []);

  const downloadQR = (url, prefix) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${prefix}_${user?.name || '销售'}.png`;
    a.click();
    message.success('二维码已下载');
  };

  const copyLink = (text) => {
    if (!text) return message.warning('暂无链接可复制');
    navigator.clipboard.writeText(text);
    message.success('链接已复制到剪贴板');
  };

  const handleSyncExternal = async () => {
    setSyncLoading(true);
    try {
      const res = await wecomApi.syncExternalContacts();
      message.success(`同步完成：新增 ${res.addedCount} 个客户，更新 ${res.updatedCount} 个`);
    } catch (e) {
      message.error(e.response?.data?.error || '同步失败');
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <Title level={4}>企微获客</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        客户扫描您的企微"联系我"二维码添加好友，系统自动创建客户记录，无需手动录入
      </Text>

      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* 企微"联系我"二维码卡片 */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, textAlign: 'center', minWidth: 300 }}
          title={<span><WechatOutlined style={{ color: '#07c160', marginRight: 8 }} />企微"联系我"二维码</span>}
        >
          {wecomError && (
            <Alert
              type="warning"
              message={wecomError}
              description='请先在企微管理后台配置"客户联系"功能，并同步通讯录绑定企微账号'
              style={{ marginBottom: 16, textAlign: 'left' }}
              showIcon
            />
          )}
          {wecomQR?.qrCode ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <Tag color="green">企微联系我</Tag>
                <Text style={{ marginLeft: 8, fontWeight: 500 }}>{user?.name}</Text>
              </div>
              <Image
                src={wecomQR.qrCode}
                width={200}
                height={200}
                preview={false}
                style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
              />
              <Paragraph type="secondary" style={{ fontSize: 12, margin: '12px 0 16px' }}>
                客户扫码 → 添加您为企微好友 → 自动录入CRM
              </Paragraph>
              <Space>
                <Button icon={<DownloadOutlined />} onClick={() => downloadQR(wecomQR.qrCode, '企微联系我')}>
                  下载图片
                </Button>
                <Button
                  type="primary"
                  icon={<ShareAltOutlined />}
                  onClick={() => copyLink(wecomQR.url)}
                  style={{ background: '#07c160', borderColor: '#07c160' }}
                >
                  复制链接
                </Button>
              </Space>
            </>
          ) : !wecomError ? (
            <div style={{
              width: 200, height: 200, border: '2px dashed #d9d9d9', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', color: '#bbb',
            }}>
              <QrcodeOutlined style={{ fontSize: 48 }} />
            </div>
          ) : null}
        </Card>

        {/* 工作原理 + 使用说明 */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <Card title="工作原理" bordered={false} style={{ borderRadius: 16, marginBottom: 16 }}>
            <Steps direction="vertical" size="small" current={-1}
              items={[
                { title: '客户扫码', description: '客户用微信扫描您的"联系我"二维码' },
                { title: '添加好友', description: '客户确认添加您为企业微信好友' },
                { title: '自动录入', description: '系统接收企微回调，自动创建客户+联系人记录' },
                { title: '通知销售', description: '您收到站内通知：新客户已自动录入' },
              ]}
            />
          </Card>

          <Card title="使用方式" bordered={false} style={{ borderRadius: 16, marginBottom: 16 }}>
            <div style={{ lineHeight: 2 }}>
              <div>① 下载或截图"联系我"二维码</div>
              <div>② 在名片/展会/朋友圈/邮件签名中展示</div>
              <div>③ 客户用微信扫码 → 自动添加您为企微好友</div>
              <div>④ 系统自动创建客户记录，来源标记为"企微"</div>
              <div>⑤ 您收到新客户通知，可直接开始跟进</div>
            </div>
          </Card>

          <Card title="手动同步" bordered={false} style={{ borderRadius: 16, marginBottom: 16 }}>
            <Paragraph type="secondary" style={{ fontSize: 13 }}>
              如果回调未及时触发，可手动同步企微外部联系人到CRM系统。
            </Paragraph>
            <Button
              icon={<SyncOutlined spin={syncLoading} />}
              loading={syncLoading}
              onClick={handleSyncExternal}
            >
              同步企微外部联系人
            </Button>
          </Card>

          {/* 小程序二维码（备用方案） */}
          {miniappQR && (
            <Card
              title={<span><SwapOutlined style={{ marginRight: 8 }} />备用：小程序二维码</span>}
              bordered={false}
              style={{ borderRadius: 16 }}
            >
              <Alert
                type="info"
                message='小程序二维码为旧版获客方式，需要客户手动填写信息。推荐使用企微"联系我"二维码。'
                style={{ marginBottom: 12 }}
                showIcon
              />
              {miniappQR.qrCode ? (
                <>
                  <Image
                    src={miniappQR.qrCode}
                    width={160}
                    height={160}
                    preview={false}
                    style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Space>
                      <Button size="small" icon={<DownloadOutlined />}
                        onClick={() => downloadQR(miniappQR.qrCode, '小程序码')}>
                        下载
                      </Button>
                      <Button size="small" icon={<ShareAltOutlined />}
                        onClick={() => copyLink(miniappQR.miniappUrl)}>
                        复制链接
                      </Button>
                    </Space>
                  </div>
                </>
              ) : (
                <Text type="secondary">小程序二维码暂不可用</Text>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}